import { useMemo } from "react"
import { LoanCalculations, MCGBLoanCalculations, StatusData } from "@oneethos/shared"
import { useAppState } from "."

type LoanData = {
  rate: number
  docStamps: number
  fees: number
  loanModFee: number
  totalClosingCosts: number
  amortization: ReturnType<typeof LoanCalculations.getPaymentAndAmortizationSchedule>
  estimatedApr: number
  mcgb: ReturnType<typeof MCGBLoanCalculations.calculate>
  estimatedMcgbApr: number
  mcgbProduct: any
  financeCharge: number
  sumOfTotalPayments: number
}

const useLoanData = (statusData: StatusData): LoanData => {
  const { tenant } = useAppState()
  const product = tenant.config.products.find(p => p.id === statusData.productId)

  return useMemo(() => {
    const rate = statusData.useAutoDebit === 'no'
      ? product.interestRateWithoutAutopay
      : product.interestRateWithAutopay

    const state = statusData.installationAddress?.state

    const { docStamps, fees, loanModFee = 0 } = LoanCalculations.estimatedClosingCostsDetail(
      statusData.loanAmount,
      state,
      statusData.projectType,
      statusData.loanModFee,
      LoanCalculations.feesForApplicationOrQuote(statusData.fees, undefined, statusData.projectType, true)
    )

    const totalClosingCosts = docStamps + fees + (statusData.feeCoveredBy === 'consumer' ? loanModFee : 0)

    const amortization = LoanCalculations.getPaymentAndAmortizationSchedule(
      statusData.loanDurationMonths,
      statusData.loanAmount,
      totalClosingCosts,
      rate,
      product.interestOnlyPeriod
    )

    const estimatedApr = LoanCalculations.apr(
      statusData.loanAmount + (state === 'MD' ? 0 : (docStamps || 0)),
      statusData.loanDurationMonths,
      amortization.payment
    )

    const mcgb = MCGBLoanCalculations.calculate(
      statusData.loanDurationMonths,
      statusData.loanAmount,
      totalClosingCosts,
      rate,
    )

    const estimatedMcgbApr = MCGBLoanCalculations.calculateIncentiveAPR(
      statusData.loanAmount,
      mcgb.incentiveSchedule.map(item => item.customerPaymentAmount)
    )

    const mcgbProduct = statusData.incentive?.incentiveProducts.find(
      p => p.loanTerm === statusData.loanDurationMonths
    )

    const financeCharge = statusData.incentiveSelected
      ? mcgb.customerFinanceCharge
      : amortization.financeCharge

    const sumOfTotalPayments = statusData.incentiveSelected
      ? mcgb.customerTotalOfPayments
      : amortization.sumOfTotalPayments

    return {
      rate,
      docStamps,
      fees,
      loanModFee,
      totalClosingCosts,
      amortization,
      estimatedApr,
      mcgb,
      estimatedMcgbApr,
      mcgbProduct,
      financeCharge,
      sumOfTotalPayments
    }
  }, [statusData, product, statusData.fees])
}

export default useLoanData